import { restApi } from '../../api/restApiSlice';
import { type StatusCodeClass } from '../StatusCodeClass';

/**
 * The four KPI-card values, measured upstream as one set decomposition — the
 * three buckets are mutually exclusive and sum to `urlsAnalyzed`. No
 * previous-period deltas — period comparisons are post-MVP.
 */
export type SearchPerformanceKpisResponse = {
  urlsAnalyzed: number;
  crawledWithImpressions: number;
  crawledNoImpressions: number;
  notServedWithImpressions: number;
};

export type SearchPerformancePathSortBy =
  | 'path'
  | 'crawledWithImpressions'
  | 'crawledNoImpressions'
  | 'notServedWithImpressions'
  | 'ctr'
  | 'errors';

export type SearchPerformancePathRow = {
  path: string;
  crawledWithImpressions: number;
  crawledNoImpressions: number;
  notServedWithImpressions: number;
  ctr: number | null;
  errors: number | null;
};

export type SearchPerformancePathsResponse = {
  rows: SearchPerformancePathRow[];
  /** Unpaged row count. */
  total: number;
};

/** Public tab keys, shared verbatim with the `tab` URL param and the BFF. */
export type SearchPerformancePagesTab =
  | 'top'
  | 'with_impressions'
  | 'no_impressions'
  | 'delivery_errors'
  | 'not_prerender_served';

/**
 * `ctr` sorts the column whose response field is `ctrPct`; `last_crawled`
 * sorts `lastCrawledAt` — the BFF owns the rename to visibility's keys.
 */
export type SearchPerformancePagesSortBy = 'impressions' | 'clicks' | 'ctr' | 'last_crawled';

/**
 * Same `last_status_code_class` vocabulary as the BFF and visibility
 * (PRE-3383). Wire name stays `last_status_code_class`; `other` is the
 * non-null complement of 2xx–5xx, not 1xx alone.
 */
export type SearchPerformanceStatusCodeClass = StatusCodeClass;

/**
 * Per-URL GSC metric state, measured upstream (never inferred from nulls
 * here): `zero_in_period` is a confirmed 0, `no_gsc_history` renders `—`.
 */
export type SearchPerformancePagesMetricState =
  | 'metrics_recorded'
  | 'zero_in_period'
  | 'no_gsc_history';

export type SearchPerformancePagesSourceType = 'GSC' | 'PRERENDER';

/**
 * One row of a single visibility view (column ownership, D050): a GSC row
 * carries `metricState` + metrics with the crawl columns null; a PRERENDER row
 * carries the crawl columns with `metricState` null and null metrics. `pageId`
 * is the same for one page on both views — the join key.
 */
export type SearchPerformancePagesRow = {
  pageId: string;
  url: string;
  /** Raw first-level section key (`docs`, or `uncategorized`). */
  sectionKey: string;
  sourceType: SearchPerformancePagesSourceType;
  lastCrawledAt: string | null;
  lastStatusCode: number | null;
  metricState: SearchPerformancePagesMetricState | null;
  impressions: number | null;
  clicks: number | null;
  ctrPct: number | null;
  /** GSC rows of a request with `inspectedWithinDays` only; `coverageState` is Google's wording, verbatim. */
  inspectionVerdict: string | null;
  coverageState: string | null;
  inspectedAt: string | null;
};

export type SearchPerformancePagesResponse = {
  rows: SearchPerformancePagesRow[];
  /** Unpaged row count of the requested view with the active filters. */
  total: number;
};

/**
 * Two request shapes feed one table page. Owner: `tab` + sort + paging from
 * the view that owns the sort — its rows and `total` are the tab's. Decoration:
 * `pageIds` of that page from the other view, no tab (the set is decided).
 */
export type SearchPerformancePagesQueryArgs = {
  dateStart: string;
  dateEnd: string;
  property: string;
  sourceType: SearchPerformancePagesSourceType;
  tab?: SearchPerformancePagesTab;
  pageIds?: string[];
  /** Only a sort the source owns; the BFF 400s otherwise. Omitted = the source's default. */
  sortBy?: SearchPerformancePagesSortBy;
  sortOrder?: 'asc' | 'desc';
  urlContains?: string;
  lastStatusCodeClasses?: SearchPerformanceStatusCodeClass[];
  limit?: number;
  offset?: number;
  inspectedWithinDays?: number;
};

const searchPerformanceApi = restApi.injectEndpoints({
  endpoints: (builder) => ({
    // No cache tags: nothing in the app invalidates KPI data (it only changes
    // with the daily sync), the date-keyed cache entry rolls over with the
    // reporting window at midnight, and the property is part of the default
    // cache key, so switching properties is a distinct entry.
    getSearchPerformanceKpis: builder.query<
      SearchPerformanceKpisResponse,
      { dateStart: string; dateEnd: string; property: string }
    >({
      query: ({ dateStart, dateEnd, property }) => ({
        url: '/v3/visibility/overview/kpis',
        method: 'GET',
        params: { date_start: dateStart, date_end: dateEnd, property },
      }),
    }),
    // Same as KPIs; sort/page are in the cache key.
    getSearchPerformancePaths: builder.query<
      SearchPerformancePathsResponse,
      {
        dateStart: string;
        dateEnd: string;
        property: string;
        sortBy: SearchPerformancePathSortBy;
        sortOrder: 'asc' | 'desc';
        limit: number;
        offset: number;
      }
    >({
      query: ({ dateStart, dateEnd, property, sortBy, sortOrder, limit, offset }) => ({
        url: '/v3/visibility/overview/paths',
        method: 'GET',
        params: {
          date_start: dateStart,
          date_end: dateEnd,
          property,
          sort_by: sortBy,
          sort_order: sortOrder,
          limit,
          offset,
        },
      }),
    }),
    // Absent optionals stay `undefined` (never `null`) so fetchBaseQuery drops them.
    getSearchPerformancePagesBySource: builder.query<
      SearchPerformancePagesResponse,
      SearchPerformancePagesQueryArgs
    >({
      query: ({
        dateStart,
        dateEnd,
        property,
        sourceType,
        tab,
        pageIds,
        sortBy,
        sortOrder,
        urlContains,
        lastStatusCodeClasses,
        limit,
        offset,
        inspectedWithinDays,
      }) => ({
        url: '/v3/visibility/overview/pages-by-source',
        method: 'GET',
        params: {
          date_start: dateStart,
          date_end: dateEnd,
          property,
          source_type: sourceType,
          tab,
          page_ids: pageIds?.length ? pageIds : undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          url_contains: urlContains,
          last_status_code_class: lastStatusCodeClasses?.length
            ? lastStatusCodeClasses
            : undefined,
          limit,
          offset,
          inspected_within_days: inspectedWithinDays,
        },
      }),
    }),
  }),
});

export const {
  useGetSearchPerformanceKpisQuery,
  useGetSearchPerformancePathsQuery,
  useGetSearchPerformancePagesBySourceQuery,
} = searchPerformanceApi;
